import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  EMPLOYEE_COLUMNS,
  EXIT_DATE_COLUMN,
  MIN_COLUMN_WIDTH,
} from './employeeColumns';

// v1 stored every column's visibility, which pinned the defaults of its time;
// v2 keeps only the user's changes, so new defaults reach everyone else.
const STORAGE_KEY = 'employeeManagement.tableColumns.v2';
const LEGACY_STORAGE_KEY = 'employeeManagement.tableColumns';

// Saved choices, limited to columns that still exist
const readStored = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') return {};
    const visible = {};
    const widths = {};
    COLUMN_KEYS.forEach((key) => {
      if (typeof parsed.visible?.[key] === 'boolean') visible[key] = parsed.visible[key];
      if (Number.isFinite(parsed.widths?.[key])) widths[key] = Math.max(MIN_COLUMN_WIDTH, parsed.widths[key]);
    });
    return { visible, widths };
  } catch (error) {
    return {};
  }
};

/**
 * Which columns the employee table shows and how wide they are. Both are
 * remembered in this browser. With the status filter on "Aktif" the exit-date
 * column is hidden (it would be empty).
 */
const useEmployeeTableColumns = (statusFilter) => {
  const [visibleColumns, setVisibleColumns] = useState(() => ({ ...DEFAULT_VISIBLE_COLUMNS, ...readStored().visible }));
  const [columnWidths, setColumnWidths] = useState(() => readStored().widths || {});

  useEffect(() => {
    const changed = COLUMN_KEYS.filter((key) => visibleColumns[key] !== DEFAULT_VISIBLE_COLUMNS[key]);
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        visible: Object.fromEntries(changed.map((key) => [key, visibleColumns[key]])),
        widths: columnWidths,
      }));
    } catch (error) {
      // Storage may be unavailable (private mode); the choices still apply now.
    }
  }, [visibleColumns, columnWidths]);

  const toggleColumn = useCallback((key) => {
    setVisibleColumns((prev) => {
      // Keep at least one column on screen
      if (prev[key] && COLUMN_KEYS.filter((item) => prev[item]).length <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  const showAllColumns = useCallback(() => {
    setVisibleColumns(Object.fromEntries(COLUMN_KEYS.map((key) => [key, true])));
  }, []);

  const resetColumns = useCallback(() => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setColumnWidths({});
  }, []);

  const setColumnWidth = useCallback((key, width) => {
    setColumnWidths((prev) => ({ ...prev, [key]: Math.max(MIN_COLUMN_WIDTH, Math.round(width)) }));
  }, []);

  const resetColumnWidth = useCallback((key) => {
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const exitDateAutoHidden = String(statusFilter) === '1';

  const visibleKeys = useMemo(
    () => COLUMN_KEYS.filter((key) => visibleColumns[key] && !(key === EXIT_DATE_COLUMN && exitDateAutoHidden)),
    [visibleColumns, exitDateAutoHidden]
  );

  const getColumnWidth = useCallback((key) => columnWidths[key] || EMPLOYEE_COLUMNS[key].width, [columnWidths]);

  return {
    visibleColumns,
    visibleKeys,
    exitDateAutoHidden,
    toggleColumn,
    showAllColumns,
    resetColumns,
    getColumnWidth,
    setColumnWidth,
    resetColumnWidth,
  };
};

export default useEmployeeTableColumns;
